/**
 * Invoice Service
 *
 * Reads canonical invoices via the public internal API.
 *
 * ## Key Features
 * - Type-safe discriminated unions (ApiResult<T>)
 * - Automatic error handling via apiClient
 * - Tenant context injection via appRuntime
 * - Returns the canonical `InvoiceDTO` (dollars, ISO UTC strings, reconciled `totals`)
 *
 * @module invoice/api
 */

import { apiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime';
import type { ApiResult } from '~/lib/apiClient/types';
import type { InvoiceDTO } from '@lgc/database/types/domains/payment';

/**
 * Invoice Service
 *
 * Handles all invoice-related API operations
 */
export const invoiceService = {
  /**
   * Get invoice by ID
   *
   * Retrieves the canonical invoice (`InvoiceDTO`) including reconciled `totals`
   * (subtotal, tax, `processingFee` / `processingFeeRate`, amountPaid, …).
   *
   * **IMPORTANT**: This requires authentication. The invoice must belong to the
   * authenticated user (authorization check happens on backend).
   *
   * @param invoiceId - Invoice ID (globally unique)
   * @returns ApiResult with canonical invoice data
   *
   * @example
   * ```typescript
   * const result = await invoiceService.getInvoiceById(184);
   *
   * if (result.success) {
   *   console.log('Processing fee:', result.payload.totals.processingFee);
   * } else {
   *   console.error('Error:', result.message);
   * }
   * ```
   */
  async getInvoiceById(
    invoiceId: number
  ): Promise<ApiResult<InvoiceDTO>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    // The invoice route needs only the invoiceId in the path — no `?include`.
    return apiClient.get<InvoiceDTO>(
      `/invoices/${invoiceId}`,
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
      }
    );
  },
};
